// maths.portll.net — private evidence photos (EXIF-stripped).
//
// ALWAYS optional. A parent may add up to `max` (3) keepsake photos of the activity.
// Privacy is load-bearing: every picked image is RE-ENCODED through a <canvas> to JPEG
// before it ever leaves the device, which strips ALL EXIF metadata (GPS, timestamps,
// device) as a side effect, and we downscale the longest side so uploads stay light.
// Uploads land under a completion's storage prefix so they can be written BEFORE the
// completion doc itself; we report the storage PATHS back through `onChange` (the
// controlled value is the array of paths). Thumbnails come from a local blob URL, so
// nothing here needs a public read of the private evidence bucket.

import { useEffect, useRef, useState } from "react";
import { ref as storageRef, uploadBytes, deleteObject } from "firebase/storage";
import { storage } from "../lib/firebase.js";
import { Note, Spinner, injectStyles } from "./ui.jsx";

const MAX_DIM = 1600; // downscale longest side (keeps files small; canvas re-encode drops EXIF)
const JPEG_QUALITY = 0.85;

// Load → draw to canvas → toBlob(JPEG). The canvas round-trip is what strips EXIF.
async function fileToJpegBlob(file) {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise((resolve, reject) => {
      const im = new Image();
      im.onload = () => resolve(im);
      im.onerror = () => reject(new Error("Could not read that image."));
      im.src = url;
    });
    const w = img.naturalWidth;
    const h = img.naturalHeight;
    if (!w || !h) throw new Error("Could not read that image.");
    const scale = Math.min(1, MAX_DIM / Math.max(w, h));
    const cw = Math.max(1, Math.round(w * scale));
    const ch = Math.max(1, Math.round(h * scale));
    const canvas = document.createElement("canvas");
    canvas.width = cw;
    canvas.height = ch;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, cw, ch);
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", JPEG_QUALITY));
    if (!blob) throw new Error("Could not process that image.");
    return blob;
  } finally {
    URL.revokeObjectURL(url);
  }
}

export default function PhotoUpload({ uid, childId, completionId, value = [], onChange, max = 3 }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [previews, setPreviews] = useState({}); // storage path → local blob objectURL
  const inputRef = useRef(null);
  const seq = useRef(0);
  const previewsRef = useRef(previews);
  previewsRef.current = previews;

  // Revoke any lingering blob URLs when the component goes away.
  useEffect(
    () => () => {
      Object.values(previewsRef.current).forEach((u) => URL.revokeObjectURL(u));
    },
    []
  );

  const paths = Array.isArray(value) ? value : [];
  const ready = !!(uid && childId && completionId);
  const full = paths.length >= max;

  async function onPick(e) {
    const files = Array.from(e.target.files || []);
    e.target.value = ""; // let the same file be re-picked later
    if (!files.length || !ready) return;
    setError(null);
    setBusy(true);
    const room = Math.max(0, max - paths.length);
    const picked = files.slice(0, room);
    const addedPaths = [];
    const addedPreviews = {};
    try {
      for (const file of picked) {
        const blob = await fileToJpegBlob(file);
        const n = seq.current++;
        const path = `evidence/${uid}/${childId}/${completionId}-${n}.jpg`;
        await uploadBytes(storageRef(storage, path), blob, { contentType: "image/jpeg" });
        addedPaths.push(path);
        addedPreviews[path] = URL.createObjectURL(blob);
      }
      if (addedPaths.length) {
        setPreviews((prev) => ({ ...prev, ...addedPreviews }));
        onChange && onChange([...paths, ...addedPaths]);
      }
    } catch (err) {
      Object.values(addedPreviews).forEach((u) => URL.revokeObjectURL(u));
      setError(err?.message || "That photo didn't upload. You can try again or skip it — photos are optional.");
    } finally {
      setBusy(false);
    }
  }

  async function remove(path) {
    onChange && onChange(paths.filter((p) => p !== path));
    setPreviews((prev) => {
      if (prev[path]) URL.revokeObjectURL(prev[path]);
      const next = { ...prev };
      delete next[path];
      return next;
    });
    // Best-effort: the doc isn't written yet, so this object is safe to drop.
    try {
      await deleteObject(storageRef(storage, path));
    } catch {
      /* ignore — object may already be gone */
    }
  }

  return (
    <div className="pu-root">
      <div className="pu-grid">
        {paths.map((p) => (
          <div className="pu-thumb" key={p}>
            {previews[p] ? (
              <img className="pu-thumb__img" src={previews[p]} alt="Activity photo you added" />
            ) : (
              <span className="pu-thumb__ph" aria-hidden="true">
                🖼️
              </span>
            )}
            <button
              type="button"
              className="pu-remove"
              onClick={() => remove(p)}
              aria-label="Remove this photo"
            >
              Remove
            </button>
          </div>
        ))}

        {!full && (
          <button
            type="button"
            className="pu-add"
            onClick={() => inputRef.current && inputRef.current.click()}
            disabled={!ready || busy}
          >
            {busy ? (
              <Spinner size={22} label="Adding photo" />
            ) : (
              <>
                <span className="pu-add__plus" aria-hidden="true">
                  ＋
                </span>
                <span>Add a photo</span>
              </>
            )}
          </button>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={onPick}
        className="pu-input"
        tabIndex={-1}
      />

      {error && <Note tone="warn">{error}</Note>}
      <p className="pu-privacy">
        <span aria-hidden="true">🔒</span> Photos stay private to your family. Delete them anytime.
      </p>
    </div>
  );
}

const PU_CSS = `
.pu-root{display:flex;flex-direction:column;gap:.6rem;}
.pu-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(120px,1fr));gap:.75rem;}
.pu-thumb{position:relative;aspect-ratio:1/1;border-radius:12px;overflow:hidden;background:var(--card);
  border:1px solid var(--line);display:grid;place-items:center;}
.pu-thumb__img{width:100%;height:100%;object-fit:cover;display:block;}
.pu-thumb__ph{font-size:2rem;}
.pu-remove{position:absolute;left:6px;right:6px;bottom:6px;min-height:38px;padding:.3rem .5rem;
  border-radius:9px;background:rgba(15,23,42,.72);color:#fff;border:0;font-weight:600;font-size:.85rem;
  backdrop-filter:blur(2px);}
.pu-remove:active{transform:translateY(1px);}
.pu-add{aspect-ratio:1/1;min-height:120px;display:flex;flex-direction:column;align-items:center;
  justify-content:center;gap:.35rem;border:2px dashed color-mix(in srgb,var(--brand) 40%,#fff);
  border-radius:12px;background:color-mix(in srgb,var(--brand) 5%,#fff);color:var(--brand-ink);
  font-weight:600;}
.pu-add:active{transform:translateY(1px);}
.pu-add:disabled{opacity:.55;}
.pu-add__plus{font-size:1.8rem;line-height:1;}
.pu-input{position:absolute;width:1px;height:1px;opacity:0;pointer-events:none;overflow:hidden;}
.pu-privacy{margin:.1rem 0 0;color:var(--ink-soft);font-size:.85rem;display:flex;align-items:center;gap:.35rem;}
`;

injectStyles("maths-photoupload-styles", PU_CSS);
